import axios from "axios";
import { memo, useContext, useEffect, useState } from "react";

import BB_Button from "@/Components/BB_Button";
import BB_ImageWrapper from "@/Components/BB_ImageWrapper";
import BB_PaymentLogo, { BB_PaymentIcon } from "@/Components/BB_PaymentLogo";
import { ModalContext } from "@/ContextProviders/ModalProvider";
import { SizeContext } from "@/ContextProviders/SizeProvider";
import BellIcon from "@/Icons/bell.svg?react";
import CartIcon from "@/Icons/cart.svg?react";
import ChevronRightIcon from "@/Icons/chevron_right.svg?react";
import NoIcon from "@/Icons/no.svg?react";
import MegaphoneIcon from "@/Icons/megaphone.svg?react";
import QuestionCircleIcon from "@/Icons/questionCircle.svg?react";
import RemoveBellIcon from "@/Icons/removeBell.svg?react";
import SpinnerIcon from "@/Icons/spinner.svg?react";
import BB_Link from "@/types/BB_Link";
import BB_Product from "@/types/BB_Product";
import { usePage } from "@inertiajs/react";

const isSSR = import.meta.env.SSR;

export type Store = {
	id: string;
	storeImage: string;
	name: string;
	itemName: string;
	url: string;
	date: string;
	payments: BB_PaymentIcon[] | null;
	status: "available" | "notify" | "unknown";
};

export type LinkListProps = {
	links: BB_Link[];
	product: BB_Product;
	registerForPush: () => void;
	isSubscribeAllLoading: boolean;
	followedAllProducts: (followed: boolean) => void;

	buttonsDisabled: boolean;
	setButtonsDisabled: (disabled: boolean) => void;

	setShowUnsubscribeAllButton: (show: boolean) => void;
	toggleFollowedState: (link: BB_Link) => void;
	needsLogin(): boolean;
};

const PrimaryButton = ({
	link,
	product,
	isSubscribeAllLoading,
	registerForPush,
	followedAllProducts,
	buttonsDisabled,
	setButtonsDisabled,
	setShowUnsubscribeAllButton,
	toggleFollowedState,
	needsLogin
}: {
	link: BB_Link;
	product: BB_Product;
	isSubscribeAllLoading: boolean;
	registerForPush: () => void;
	followedAllProducts: (followed: boolean) => void;
	buttonsDisabled: boolean;
	setButtonsDisabled: (disabled: boolean) => void;
	setShowUnsubscribeAllButton: (show: boolean) => void;
	toggleFollowedState: (link: BB_Link) => void;
	needsLogin(): boolean;
}) => {
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		setIsLoading(isSubscribeAllLoading);
	}, [isSubscribeAllLoading]);

	const onNotifyClick = async (e?: MouseEvent | TouchEvent) => {
		e?.stopPropagation();
		e?.preventDefault();

		if (needsLogin()) {
			return;
		}

		if (isLoading) return;
		setIsLoading(true);
		setButtonsDisabled(true);

		if (!link.followed) registerForPush();

		try {
			const response = await axios.post(
				`/api/follow/link/${link.id}/${link.followed ? "unfollow" : "follow"}`,
				{ productId: product.id }
			);

			if (response.status === 200) {
				const jsonResponse = response.data;

				setShowUnsubscribeAllButton(jsonResponse.followed_any_links);

				// Hide unfollow_all_button if all_products gets returned
				if (jsonResponse.all_followed) {
					setShowUnsubscribeAllButton(jsonResponse.all_followed);
				}

				followedAllProducts(link.followed ? false : jsonResponse.all_followed);
				toggleFollowedState(link);
			} else {
				console.error("[🧨] Cannot follow/unfollow product");
			}
		} catch (error: unknown) {
			console.error("[🧨] Error following/unfollowing product", error);
		}

		setIsLoading(false);
		setButtonsDisabled(false);
	};

	if (link.is_search_link) return null;

	return (
		<BB_Button
			type={link.followed ? "unknown" : "notify"}
			title={isLoading ? "" : (link.followed && "Deaktivieren") || "Alarmieren"}
			icon={isLoading ? SpinnerIcon : (link.followed && RemoveBellIcon) || BellIcon}
			iconClassName={`h-5 w-5 sm:mr-2 ${
				isLoading
					? "pointer-events-none text-gray-200 animate-spin dark:text-gray-600 fill-primary-500"
					: ""
			}`}
			onClick={onNotifyClick}
			buttonClassName="w-35"
			disabled={buttonsDisabled}
			hideText={false}
		/>
	);
};

const SecondaryButton = ({ link }: { link: BB_Link }) => {
	if (link.is_search_link) return null;

	switch (link.in_stock) {
		case 1:
			return (
				<BB_Button
					type="available"
					title="Bestellbar"
					icon={CartIcon}
					iconClassName="h-5 w-5 mr-2 pointer-events-none"
					buttonClassName="w-35"
					hideText={false}
				/>
			);
		case 0:
			return (
				<BB_Button
					type="unknown"
					title="Ausverkauft"
					icon={NoIcon}
					iconClassName="h-5 w-5 mr-2 pointer-events-none"
					buttonClassName="w-35 pointer-events-none"
					hideText={false}
				/>
			);
		default:
			return (
				<BB_Button
					type="unknown"
					title="Unbekannt"
					icon={QuestionCircleIcon}
					iconClassName="h-5 w-5 mr-2 pointer-events-none"
					buttonClassName="mt-4 lg:mt-0 w-35 pointer-events-none ml-auto"
					hideText={false}
				/>
			);
	}
};

const BB_LinkList = (props: LinkListProps) => {
	const user = usePage().props?.auth?.user;
	const { isLarge } = useContext(SizeContext);
	const { openInNewTab, openLinkModeration } = useContext(ModalContext);

	if (isSSR || isLarge) {
		return (
			<div className="flex flex-col gap-4">
				{props.links.map((link, index) => {
					const price = link.full_price?.toString().split(",") ?? "-";
					const payments = link.store.payments?.slice(0, 6);

					return (
						<a
							key={link.id}
							id={"linkList_" + index.toString()}
							className="dark:bg-dark-background-400 cursor-pointer rounded-lg bg-white p-4 shadow-lg"
							href={link.go_link}
							target="_blank"
							rel="noopener nofollow noreferrer"
							title={link.link}>
							<div className="flex items-center gap-4">
								<div className="h-12 w-12 shrink-0 overflow-hidden rounded-full shadow-md">
									<BB_ImageWrapper
										bb_image={link.store.image}
										suffix="?class=1"
										addPadding={link.store.image?.trimmed === 1}
										className="h-12 w-12 flex-none bg-white object-contain"
										alt="Geschäfts Logo"
									/>
								</div>

								<div className="flex min-h-[3rem] flex-col justify-center overflow-hidden">
									<p className="text-balance text-ellipsis line-clamp-2 text-sm font-medium text-slate-800 dark:text-slate-200">
										{link.store.name}
									</p>
									<p className="text-balance text-ellipsis line-clamp-2 text-xs break-words whitespace-normal text-slate-600 dark:text-slate-400">
										{link.title}
									</p>
								</div>

								<div className="whitespace-nowrap ms-auto text-lg font-bold text-black dark:text-gray-400" aria-label={`${price[0] ?? "0"},${(price[1] ?? "0").padEnd(2, "0")}`}>
                                    { link.price && link.product?.rrp && link.product?.rrp < link.price && (
                                        <div className="px-1 w-fit mb-0.5 bg-red-500 rounded-sm text-xs font-medium text-white">
                                            Über UVP
                                        </div>
                                    )}
                                    <p>
                                        {price[0] ?? "0"}
                                        <span className="sr-only">,</span>
                                        <span className="align-top text-xs text-black dark:text-gray-400">
                                            {price[0] !== "-"
                                                ? `${(price[1] ?? "0").padEnd(2, "0")}`
                                                : ""}
                                        </span>
                                    </p>
								</div>
							</div>

							{payments && (
								<div className="mt-4 flex items-center justify-end">
									<div className="bg-background-200 dark:bg-dark-background-200 flex gap-0.5 overflow-hidden rounded-sm">
										{payments.map((logo, index) => (
											<BB_PaymentLogo
												key={logo + "_" + index.toString()}
												name={logo}
												className={"h-5 w-auto"}
											/>
										))}
									</div>
								</div>
							)}

							<div className="flex items-center justify-end">
								<div className="flex items-center flex-wrap justify-end gap-2 mt-4">
									{ user?.is_editor && !link.is_search_link &&
										<>
											<BB_Button
												type="destructive"
												icon={MegaphoneIcon}
												iconClassName="h-5 w-5 mr-2 pointer-events-none"
												title="Alarmieren"
												buttonClassName="lg:mt-0 w-auto"
												onClick={(e) => {
													e?.stopPropagation();
													e?.preventDefault();
													openLinkModeration(link)
												}}
												hideText={false}
											/>
											<div className="w-full"></div>
										</>
									}
									<PrimaryButton
										{...props}
										link={link}
										buttonsDisabled={props.buttonsDisabled}
										setButtonsDisabled={props.setButtonsDisabled}
										setShowUnsubscribeAllButton={
											props.setShowUnsubscribeAllButton
										}
										toggleFollowedState={props.toggleFollowedState}
									/>
									<SecondaryButton link={link} />
								</div>
							</div>
						</a>
					);
				})}
			</div>
		);
	} else {
		return (
			<div className="flex flex-col overflow-hidden rounded-lg bg-clip-padding shadow-lg">
				<table className="w-full table-auto text-left">
					<thead>
						<tr className="dark:border-dark-background-200 bg-background-200 dark:bg-dark-background-200 border-b border-gray-300">
							<th className="dark:border-dark-background-300 border-e-2 border-gray-300 p-4">
								<p className="flex items-center justify-between gap-2 text-sm leading-none font-bold text-slate-800 dark:text-slate-200">
									Händler Informationen
								</p>
							</th>
							<th className="dark:border-dark-background-300 border-0 border-gray-300 p-0 lg:border-e-2 lg:p-4" />
							<th className="dark:border-dark-background-300 border-e-2 border-gray-300 p-4">
								<p className="flex items-center justify-center gap-2 text-sm leading-none font-bold text-slate-800 dark:text-slate-200">
									Preis
								</p>
							</th>
							<th className="p-4">
								<p className="flex items-center justify-center gap-2 text-sm leading-none font-bold text-slate-800 dark:text-slate-200">
									Status
								</p>
							</th>
						</tr>
					</thead>
					<tbody className="select-none">
						{props.links.map((link, index) => {
							const price = link.full_price?.toString().split(",") ?? "-";
							const payments = link.store.payments?.slice(0, 6);

							return (
								<tr
									title={link.link}
									key={link.id}
									id={"linkList_" + index.toString()}
									onClick={() => {
										if (link.go_link) {
											openInNewTab(link.go_link);
										}
									}}
									className={`${
										props.links.length - 1 === index
											? "border-b-0"
											: "border-b-2"
									} dark:border-dark-background-200 bg-background-50 dark:bg-dark-background-400 hover:bg-background-100 dark:hover:bg-dark-background-350 cursor-pointer border-gray-300`}>
									<td className="w-full py-4 ps-4">
										<div className="flex items-start gap-4">
											<div className="shrink-0 hidden h-12 w-12 overflow-hidden rounded-full shadow-md sm:flex">
												<BB_ImageWrapper
													bb_image={link.store.image}
													suffix="?class=1"
													addPadding={link.store.image?.trimmed === 1}
													className="hidden h-12 w-12 flex-none bg-white object-contain sm:flex"
													alt="Geschäfts Logo"
												/>
											</div>
											<a
												href={link.go_link}
												onClick={(e) => e.preventDefault()}
												rel="noopener nofollow"
												className="flex min-h-12 flex-col justify-center"
												title={link.link}>
												<p className="text-balance text-ellipsis line-clamp-2 -sm font-medium text-slate-800 dark:text-slate-200">
													{link.store.name}
												</p>
												<p className="text-balance text-ellipsis line-clamp-2 text-xs text-slate-600 dark:text-slate-400">
													{link.title}
												</p>
											</a>
										</div>
									</td>
									<td className="p-0 lg:p-2">
										<div className="bg-background-200 dark:bg-dark-background-200 hidden overflow-hidden rounded-md lg:flex">
											<div className="grid auto-cols-max grid-flow-col grid-rows-2 gap-0.5">
												{payments?.map((logo, index) => (
													<BB_PaymentLogo
														key={logo + "_" + index.toString()}
														name={logo}
														className="h-5 w-auto overflow-hidden"
													/>
												))}
											</div>
										</div>
									</td>
									<td className="p-4 text-right whitespace-nowrap">
                                        { link.price && link.product?.rrp && link.product?.rrp < link.price && (
                                            <div className="px-1 w-fit mb-0.5 bg-red-500 rounded-sm text-xs font-medium text-white">
                                                Über UVP
                                            </div>
                                        )}
										<p className="text-base font-bold text-black md:text-2xl dark:text-gray-400" aria-label={`${price[0] ?? "0"},${(price[1] ?? "0").padEnd(2, "0")}`} >
											{price[0]}
											<span className="sr-only">,</span>
											<span className="text-xxs align-top text-black md:text-sm dark:text-gray-400">
												{price[0] !== "-"
													? `${(price[1] ?? "0").padEnd(2, "0")}`
													: ""}
											</span>
										</p>
									</td>
									<td className="p-4 whitespace-nowrap">
										<div className="flex items-center justify-end gap-2">
											{ user?.is_editor && !link.is_search_link &&
												<BB_Button
													type="destructive"
													icon={MegaphoneIcon}
													iconClassName="h-5 w-5 pointer-events-none"
													buttonClassName="w-auto"
													hideText={false}
													onClick={(e) => {
														e?.stopPropagation();
														e?.preventDefault();
														openLinkModeration(link)
													}}
												/>
											}
											<PrimaryButton
												{...props}
												link={link}
												buttonsDisabled={props.buttonsDisabled}
												setButtonsDisabled={props.setButtonsDisabled}
												setShowUnsubscribeAllButton={
													props.setShowUnsubscribeAllButton
												}
												toggleFollowedState={props.toggleFollowedState}
												needsLogin={props.needsLogin}
											/>
											<SecondaryButton link={link} />
											<ChevronRightIcon className="ms-0 ml-auto hidden h-6 w-4 text-black md:ms-2 md:block dark:text-white" />
										</div>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		);
	}
};

export default memo(BB_LinkList);
